import { supabase } from '@/lib/supabase';
import { Review, ReviewImage } from '@/types';

export interface CreateReviewRequest {
  order_id: string;
  restaurant_id: string;
  rating: number; // 0.5-5.0 (0.5 단위)
  comment: string;
  is_anonymous?: boolean;
  images?: File[];
}

export interface UpdateReviewRequest {
  rating?: number;
  comment?: string;
  images?: File[];
}

class ReviewService {
  async createReview(
    userId: string,
    request: CreateReviewRequest
  ): Promise<{ success: boolean; error?: string; review?: Review }> {
    try {
      // Validate order completion
      const { data: order } = await supabase
        .from('orders')
        .select('id, status, buyer_id')
        .eq('id', request.order_id)
        .eq('buyer_id', userId)
        .single();

      if (!order) {
        return { success: false, error: '주문을 찾을 수 없습니다.' };
      }

      if (order.status !== 'COMPLETED') {
        return { success: false, error: '수령 완료된 주문만 리뷰를 작성할 수 있습니다.' };
      }

      // Check if review already exists
      const { data: existingReview } = await supabase
        .from('reviews')
        .select('id')
        .eq('order_id', request.order_id)
        .eq('user_id', userId)
        .maybeSingle();

      if (existingReview) {
        return { success: false, error: '이미 리뷰를 작성하셨습니다.' };
      }

      // Validate rating (0.5-5.0 scale, 0.5 unit)
      if (request.rating < 0.5 || request.rating > 5 || request.rating % 0.5 !== 0) {
        return { success: false, error: '별점은 0.5-5.0 사이, 0.5점 단위로 입력해주세요.' };
      }

      // Validate comment length
      if (!request.comment || request.comment.length < 5 || request.comment.length > 200) {
        return { success: false, error: '리뷰는 5자 이상 200자 이하로 작성해주세요.' };
      }

      // Get user nickname from buyer_profiles
      const { data: buyerProfile } = await supabase
        .from('buyer_profiles')
        .select('nickname')
        .eq('user_id', userId)
        .single();

      const reviewerName = request.is_anonymous
        ? '익명'
        : buyerProfile?.nickname || '익명';

      // Create review
      const { data: reviewData, error: reviewError } = await supabase
        .from('reviews')
        .insert({
          user_id: userId,
          restaurant_id: request.restaurant_id,
          order_id: request.order_id,
          rating: Math.round(request.rating * 2), // UI 0.5-5.0 → DB 1-10 변환
          comment: request.comment,
          reviewer_nickname: reviewerName,
          is_anonymous: request.is_anonymous ?? false,
          is_verified: true // Order-based review
        })
        .select()
        .single();

      if (reviewError) {
        console.error('Error creating review:', reviewError);
        return { success: false, error: '리뷰 작성에 실패했습니다.' };
      }

      // Upload images if provided
      if (request.images && request.images.length > 0) {
        if (request.images.length > 3) {
          return { success: false, error: '사진은 최대 3장까지 첨부할 수 있습니다.' };
        }

        const uploadedImages = await this.uploadReviewImages(
          reviewData.id,
          request.images
        );

        if (!uploadedImages.success) {
          // Rollback review creation
          await supabase.from('reviews').delete().eq('id', reviewData.id);
          return { success: false, error: uploadedImages.error };
        }

        reviewData.images = uploadedImages.images;
      }

      return { success: true, review: reviewData };
    } catch (error) {
      console.error('Error creating review:', error);
      return { success: false, error: '리뷰 작성 중 오류가 발생했습니다.' };
    }
  }

  async uploadReviewImages(
    reviewId: string,
    files: File[]
  ): Promise<{ success: boolean; error?: string; images?: ReviewImage[] }> {
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) {
        return { success: false, error: '인증이 필요합니다.' };
      }

      const functionsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/image-resize-upload`;
      const uploadedImages: ReviewImage[] = [];

      for (let i = 0; i < files.length; i++) {
        const form = new FormData();
        form.append('file', files[i]);
        form.append('category', 'review');
        form.append('entity_id', reviewId);
        form.append('sequence', String(i + 1));

        const res = await fetch(functionsUrl, {
          method: 'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
          body: form
        });
        const json = await res.json();
        if (!res.ok || !json?.success) {
          console.error('Edge upload review image error:', json);
          return { success: false, error: json?.error || '이미지 업로드에 실패했습니다.' };
        }
        if (json.image) {
          uploadedImages.push(json.image as ReviewImage);
        }
      }

      return { success: true, images: uploadedImages };
    } catch (error) {
      console.error('Error uploading review images:', error);
      return { success: false, error: '이미지 업로드 중 오류가 발생했습니다.' };
    }
  }

  async getReviewByOrderId(
    orderId: string,
    userId: string
  ): Promise<Review | null> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          images:review_images(id, review_id, image_url, sequence, created_at)
        `)
        .eq('order_id', orderId)
        .eq('user_id', userId)
        .maybeSingle();

      if (error || !data) {
        return null;
      }

      // DB 1-10 → UI 0.5-5.0 변환
      data.rating = data.rating / 2;

      return data;
    } catch (error) {
      console.error('Error fetching review:', error);
      return null;
    }
  }

  async getUserReviews(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          restaurant:restaurants(id, name, image_url),
          images:review_images(id, review_id, image_url, sequence, created_at)
        `)
        .eq('user_id', userId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching user reviews:', error);
        return [];
      }

      // DB 1-10 → UI 0.5-5.0 변환
      return (data || []).map(review => ({
        ...review,
        rating: review.rating / 2
      }));
    } catch (error) {
      console.error('Error fetching user reviews:', error);
      return [];
    }
  }

  async updateReview(
    reviewId: string,
    userId: string,
    request: UpdateReviewRequest
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const updateData: any = {};

      if (request.rating !== undefined) {
        if (request.rating < 0.5 || request.rating > 5 || request.rating % 0.5 !== 0) {
          return { success: false, error: '별점은 0.5-5.0 사이, 0.5점 단위로 입력해주세요.' };
        }
        updateData.rating = Math.round(request.rating * 2);
      }

      if (request.comment !== undefined) {
        if (request.comment.length < 5 || request.comment.length > 200) {
          return { success: false, error: '리뷰는 5자 이상 200자 이하로 작성해주세요.' };
        }
        updateData.comment = request.comment;
      }

      const { error } = await supabase
        .from('reviews')
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .eq('user_id', userId);

      if (error) {
        return { success: false, error: '리뷰 수정에 실패했습니다.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating review:', error);
      return { success: false, error: '리뷰 수정 중 오류가 발생했습니다.' };
    }
  }

  async deleteReview(
    reviewId: string,
    userId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Soft delete by updating status
      const { error } = await supabase
        .from('reviews')
        .update({
          status: 'deleted',
          updated_at: new Date().toISOString()
        })
        .eq('id', reviewId)
        .eq('user_id', userId);

      if (error) {
        return { success: false, error: '리뷰 삭제에 실패했습니다.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting review:', error);
      return { success: false, error: '리뷰 삭제 중 오류가 발생했습니다.' };
    }
  }

  async getReviewsByRestaurant(
    restaurantId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<Review[]> {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select(`
          *,
          images:review_images(id, review_id, image_url, sequence, created_at)
        `)
        .eq('restaurant_id', restaurantId)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching restaurant reviews:', error);
        return [];
      }

      // DB 1-10 → UI 0.5-5.0 변환
      return (data || []).map(review => ({
        ...review,
        rating: review.rating / 2
      }));
    } catch (error) {
      console.error('Error fetching restaurant reviews:', error);
      return [];
    }
  }

  async reportReview(
    reviewId: string,
    reporterId: string,
    reason: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('review_reports')
        .insert({
          review_id: reviewId,
          reporter_id: reporterId,
          reason: reason,
          status: 'pending'
        });

      if (error) {
        return { success: false, error: '신고 접수에 실패했습니다.' };
      }

      return { success: true };
    } catch (error) {
      console.error('Error reporting review:', error);
      return { success: false, error: '신고 접수 중 오류가 발생했습니다.' };
    }
  }
}

export const reviewService = new ReviewService();
